import {
  Button,
  ButtonGroup,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Code as OrbitCode,
  Text,
} from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom';

import { parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { variantHomepageRouteConfig } from 'src/app/graph/routes';
import { Code } from 'src/components/code/Code';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { DEFAULT_VARIANT_NAME } from 'src/lib/graphOnboarding/constants';
import { useLDFlag } from 'src/lib/launchDarkly';
import { downloadSubgraph, runRoverDev } from 'src/lib/roverCommands';

interface LocalDevStepContentProps {
  subgraphName: string;
  graphRef: string;
  serviceApiKey: string;
  onClose: () => void;
  footerContent?: React.ReactNode;
}

export const LocalDevStepContent = ({
  subgraphName,
  graphRef,
  serviceApiKey = '',
  onClose,
}: LocalDevStepContentProps) => {
  const federationVersion = useLDFlag(
    'growth-connectors-onboarding-fed-version',
  );
  const routerVersion = useLDFlag('connectors_router_version');
  const { graphId, graphVariant = DEFAULT_VARIANT_NAME } =
    parseGraphRef(graphRef) ?? {};

  return (
    <ModalContent>
      <ModalHeader>
        <ModalTitle>Set up your local development environment</ModalTitle>
        <ModalDescription>
          Follow these steps to start a local development session with an
          example GraphQL schema that uses Apollo Connectors.
        </ModalDescription>
      </ModalHeader>

      <ModalBody className="pl-0">
        <ol className="flex flex-col gap-4">
          <li>
            <Text>
              <a
                className="font-semibold underline"
                href="https://www.apollographql.com/docs/rover/getting-started"
                target="_blank"
                rel="noreferrer"
              >
                Install the latest version of the Rover CLI
              </a>
              .
            </Text>
          </li>
          <li>
            <Text>
              Create a <OrbitCode>supergraph.yaml</OrbitCode> file in a new
              directory and add the following code:
            </Text>
          </li>
          <Code
            className="mb-2"
            title="supergraph.yaml"
            showCopyButton
            code={`federation_version: =${federationVersion}
subgraphs:
  ${subgraphName}:
    routing_url: http://ignored
    schema:
      file: ${subgraphName}.graphql`}
          />
          <li>
            <Text>
              Download the example schema by running the following command in
              the terminal:
            </Text>
          </li>
          <Code
            className="mb-2"
            showCopyButton
            mask={serviceApiKey.split(':').pop()}
            code={
              downloadSubgraph({
                apiKey: serviceApiKey,
                graphRef,
                subgraphName,
              }).shellString
            }
          />
          <li>
            <Text>
              Run <OrbitCode>rover dev</OrbitCode> to start the local
              development session:
            </Text>
          </li>
          <Code
            className="mb-2"
            showCopyButton
            mask={serviceApiKey.split(':').pop()}
            code={
              runRoverDev({
                apiKey: serviceApiKey,
                graphRef,
                routerVersion,
              }).shellString
            }
          />
          <li>
            <Text>
              You’re all set here! Head over to the{' '}
              <a
                href="http://go.apollo.dev/connectors/quickstart"
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline"
                onClick={async () => {
                  ampli.connectorsQuickstartClicked({
                    'API Type': 'REST',
                    OnboardingVariant: 'GraphOS-Trial',
                  });
                }}
              >
                Connectors Quickstart
              </a>{' '}
              to learn how to query your graph using Apollo Sandbox.
            </Text>
          </li>
        </ol>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup>
          {graphId && (
            <Button
              as={Link}
              to={variantHomepageRouteConfig.location({
                graphId,
                graphVariant,
              })}
              onClick={async () => {
                ampli.goToGraphClicked({
                  'API Type': 'REST',
                  OnboardingVariant: 'GraphOS-Trial',
                });
              }}
              variant="secondary"
            >
              Go to graph
            </Button>
          )}
          <Button
            onClick={async () => {
              ampli.closeClicked({
                'API Type': 'REST',
                OnboardingVariant: 'GraphOS-Trial',
              });
              onClose();
            }}
            variant="primary"
          >
            Close
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </ModalContent>
  );
};
